import { useCallback, useRef, useState } from "react";

export interface UseDialogStateOptions<T> {
  onOpen?: (payload: T) => void;
  onClose?: () => void;
}

/** Evento de UI (DOM ou SyntheticEvent do React) que chega quando `open` é passado direto como handler. */
function isUiEvent(value: unknown): boolean {
  if (typeof Event !== "undefined" && value instanceof Event) return true;
  return (
    typeof value === "object" &&
    value !== null &&
    "nativeEvent" in value &&
    typeof (value as { preventDefault?: unknown }).preventDefault === "function"
  );
}

/** `open()` sem argumento quando `T` é `void`; com `T`, o payload é obrigatório. */
type OpenArgs<T> = [T] extends [void] ? [] : [payload: T];

export interface UseDialogStateResult<T> {
  isOpen: boolean;
  /** `null` com o dialog fechado (limpo no `close`, sem esperar a animação) e quando `T` é `void`. */
  payload: T | null;
  open: (...args: OpenArgs<T>) => void;
  close: () => void;
  /** Fecha se aberto; abre se fechado, sem payload (pensado para `T = void`). */
  toggle: () => void;
}

interface DialogState<T> {
  isOpen: boolean;
  payload: T | null;
}

/** Estado de um dialog: aberto/fechado e o payload tipado com que foi aberto. */
export function useDialogState<T = void>(
  options: UseDialogStateOptions<T> = {},
): UseDialogStateResult<T> {
  const [state, setState] = useState<DialogState<T>>({ isOpen: false, payload: null });
  const isOpenRef = useRef(false);
  const callbacks = useRef(options);
  callbacks.current = options;

  const openWith = useCallback((payload: T | undefined) => {
    isOpenRef.current = true;
    setState({ isOpen: true, payload: payload ?? null });
    callbacks.current.onOpen?.(payload as T);
  }, []);

  const open = useCallback(
    // `onClick={open}` entrega o evento como primeiro argumento: nunca é payload.
    (...args: OpenArgs<T>) => openWith(isUiEvent(args[0]) ? undefined : (args[0] as T | undefined)),
    [openWith],
  );

  const close = useCallback(() => {
    isOpenRef.current = false;
    setState({ isOpen: false, payload: null });
    callbacks.current.onClose?.();
  }, []);

  const toggle = useCallback(() => {
    if (isOpenRef.current) close();
    else openWith(undefined);
  }, [close, openWith]);

  return { isOpen: state.isOpen, payload: state.payload, open, close, toggle };
}
